import { HttpHeaders } from '@angular/common/http';
import { inject,Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { concat, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { AuthToken } from '@personal-area/global/authorization/models';
import {
  AppleLoginResult,
  AppleLoginStage,
  ExtendedSocialUser,
  ONE_TIME_TOKEN_PARAM,
} from '@personal-area/global/social-authorization/models';
import { UserFacade } from '@personal-area/global/users/services';
import { CryptoStorageService, SESSION_STORAGE } from '@personal-area/storage';

import { AppleAuthApiService } from './apple-auth.api.service';

export interface SocialNetworksServiceInterface {
  loginWithSocialData(
    socialUser: ExtendedSocialUser,
    headers: HttpHeaders
  ): Observable<AuthToken>;
}

export interface AppleLoginConfig {
  socialNetworksService: SocialNetworksServiceInterface;
  userFacade: UserFacade;
  referralHeaders: HttpHeaders;
}

const PROCESSED_TOKENS_KEY = 'apple-processed-tokens';

@Injectable({ providedIn: 'root' })
export class AppleOneTimeTokenService {
  private _appleAuthApi = inject(AppleAuthApiService);
  private _router = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  private _cryptoStorage = inject(CryptoStorageService);
  private _sessionStorage = inject(SESSION_STORAGE);

  public checkAndProcessToken(token: string | undefined): Observable<ExtendedSocialUser | null> {
    if (!token) {
      return of(null);
    }

    if (this._isTokenProcessed(token)) {
      this._clearAllQueryParams();
      return of(null);
    }

    return this._appleAuthApi.getAppleInfo(token).pipe(
      tap(() => {
        this._clearOneTimeTokenFromUrl();
      }),
      catchError(() => of(null))
    );
  }

  public loginWithApple(
    token: string | undefined,
    config: AppleLoginConfig
  ): Observable<AppleLoginResult> {
    return this.checkAndProcessToken(token).pipe(
      switchMap(socialUser => {
        if (!socialUser) {
          return of({
            socialUser: null,
            authToken: null,
            stage: AppleLoginStage.NoToken
          });
        }

        return concat(
          of({
            socialUser,
            authToken: null,
            stage: AppleLoginStage.SocialUserLoaded
          }),
          config.socialNetworksService
            .loginWithSocialData(socialUser, config.referralHeaders)
            .pipe(
              tap(authToken => {
                config.userFacade.getUser(authToken.userID);
                this._markTokenAsProcessed(token);
              }),
              map(authToken => ({
                socialUser,
                authToken,
                stage: AppleLoginStage.LoginCompleted
              }))
            )
        );
      })
    );
  }

  private _isTokenProcessed(token: string): boolean {
    const processed = this._cryptoStorage.getItem(PROCESSED_TOKENS_KEY, this._sessionStorage);
    if (!processed) {
      return false;
    }

    const tokens = (JSON.parse(processed) as string[]) || [];
    return tokens.includes(token);
  }

  private _markTokenAsProcessed(token: string | undefined): void {
    if (!token) {
      return;
    }
    const processed = this._cryptoStorage.getItem(PROCESSED_TOKENS_KEY, this._sessionStorage);
    const tokens = processed ? (JSON.parse(processed) as string[]) : [];
    tokens.push(token);
    this._cryptoStorage.setItem(PROCESSED_TOKENS_KEY, JSON.stringify(tokens), this._sessionStorage);
  }

  private _clearAllQueryParams(): void {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: {},
      replaceUrl: true
    });
  }

  private _clearOneTimeTokenFromUrl(): void {
    const queryParams = { ...this._activatedRoute.snapshot.queryParams };
    delete queryParams[ONE_TIME_TOKEN_PARAM];

    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
